export interface Price {
  totalPrice: number;
  listPrice: any | null;
  unitPrice: number;
}

export interface QuantityInformation {
  minPurchase: number;
  purchaseSteps: number;
  maxPurchase: number;
}

export interface Media {
  url: string;
  alt: string;
}

export interface LineItem {
  id: string;
  type: 'product' | 'promotion' | 'bpgiftcard';
  price: Price;
  quantity: number;
  label: string;
  unitPrice: number;
  payload: any;
  removable: boolean;
  quantityInformation: QuantityInformation;
  cover: Media;
}

export type LineItems = Array<LineItem>

export interface DeliveryTime {
  name: string;
}
export interface ShippingMethod {
  id: string;
  name: string;
  active: boolean;
  media: Media;
  deliveryTime: DeliveryTime;
  extensions: Record<string, any>;
  customFields: Record<string, any>;
}

export type ShippingMethods = Array<ShippingMethod>

export interface PaymentMethod {
  id: string;
  name: string;
  active: boolean;
  description: string;
  media: Media;
}

export type PaymentMethods = Array<PaymentMethod>

export interface Delivery {
  shippingMethod: ShippingMethod;
  location: { country: { id: string } };
  [key: string]: any;
}

export type Deliveries = [Delivery, ...Array<Delivery>]

export interface Transaction {
  paymentMethodId: string;
  [key: string]: any;
}

export type Transactions = [Transaction, ...Array<Transaction>]
export interface Cart {
  deliveries: Deliveries;
  lineItems: LineItems;
  price: Price;
  transactions: Transactions;
  errors: Record<string, any>;
}

interface PromotionErrorData {
  message: string;
  [key: string]: any;
}

export class PromotionError extends Error {
  #_data: PromotionErrorData

  constructor(code: string, data: PromotionErrorData, opts?: { cause: unknown }) {
    super(code, opts)

    this.name = 'PromotionError'
    this.#_data = data

    if (opts?.cause && !this.cause) {
      this.cause = opts.cause
    }
  }

  get promotionMessage () {
    return this.#_data.message
  }

  get data () {
    return this.#_data
  }
}

export const useCartStore = defineStore('cart', {
  state: () => {
    return {
      cart: null as Cart | null,
      isUpdating: false,
      isRefreshing: false
    }
  },
  actions: {
    async init (options?: Record<string, any>): Promise<Cart> {
      const cart = await $fetch<Cart>('/store-api/checkout/cart', {
        ...options,
        headers: useRequestHeaders(['cookie']),
        method: 'POST'
      })

      this.$patch((state) => state.cart = cart)

      return cart
    },
    async addGiftCard (code: string) {
      try {
        const cart = await $fetch<Cart>('/store-api/checkout/cart/gift-card', {
          method: 'POST',
          body: {
            code
          }
        })

        return cart
      } catch (e: any) {
        if (e.data?.data?.errors) {
          const giftCardError = e.data.data.errors.find((error: { code: string }) => {
            return [
              'gift-card-code-invalid-format',
              'gift-card-already-in-cart',
              'gift-card-expired',
              'gift-card-invalid-state',
              'gift-card-not-found',
              'gift-card-insufficient-balance',
              'gift-card-invalid-sales-channel'
            ].includes(error.code)
          })
          if (giftCardError) {
            const { code, detail: message, ...details } = giftCardError
            throw new PromotionError(code, {
              message,
              ...details
            }, {
              cause: giftCardError
            })
          }
        }

        throw new Error('Wystąpił problem podczas dodawania kodu. Spróbuj ponownie później', {
          cause: e
        })
      }
    },
    async addPromotionCode (code: string) {
      try {
        const cart = await $fetch<Cart>('/store-api/checkout/cart/line-item', {
          method: 'POST',
          body: {
            items: [{
              type: 'promotion',
              referencedId: code
            }]
          }
        })

        const { errors } = cart

        const promotionError = Object.values(errors)
          .find(error => error.messageKey !== 'promotion-discount-added')
        if (promotionError) {
          const { messageKey: code, message, ...details } = promotionError
          throw new PromotionError(code, {
            message,
            ...details
          }, {
            cause: promotionError
          })
        }

        return cart

      } catch (e) {
        if (e instanceof PromotionError) {
          throw e
        }

        throw new Error('Wystąpił problem podczas dodawania kodu. Spróbuj ponownie później', {
          cause: e
        })
      }
    },
    async addPromotion (code: string) {
      if (code.match(/^(GFO|GO)/i)) {
        const cart = await this.addGiftCard(code)
        this.$patch((state) => state.cart = cart)
      } else {
        const cart = await this.addPromotionCode(code)
        this.$patch((state) => state.cart = cart)
      }
    },
    async addProduct ({ id, quantity = 1 }: { id: string, quantity?: number }): Promise<Cart> {
      const cart = await $fetch<Cart>('/store-api/checkout/cart/line-item', {
        method: 'POST',
        body: {
          items: [{
            quantity,
            type: 'product',
            referencedId: id,
            id
          }]
        },
      })

      this.$patch((state) => state.cart = cart)

      return cart
    },
    async removeItem (id: string): Promise<Partial<Cart>> {
      const cart = await $fetch<Cart>('/store-api/checkout/cart/line-item', {
        method: 'DELETE',
        params: {
          'ids[]': id
        }
      })

      this.$patch((state) => state.cart = cart)

      return cart
    },
    async updateItemQuantity({ id, quantity }: { id: LineItem['id'], quantity: number }, options?: Record<string, any>): Promise<Cart> {
      const cart = await $fetch<Cart>('/store-api/checkout/cart/line-item', {
        method: 'PATCH',
        ...options,
        body: {
          items: [{
            id,
            quantity
          }]
        }
      })

      this.$patch((state) => state.cart = cart)

      return cart
    },
    async updateContext (context: Partial<{ shippingMethodId: string, shippingAddressId: string, paymentMethodId: string }>, options? :Record<string, any>) {
      await $fetch('/store-api/context', {
        ...options,
        method: 'PATCH',
        body: context
      })
    }
  },
  getters: {
    products ({ cart }): LineItems {
      return cart?.lineItems.filter(item => item.type === 'product') || []
    },
    productQuantity (): (id: string) => number {
      return (id: string) => this.products.find(item => item.id === id)?.quantity || 0
    },
    productsQuantity (): number {
      return this.products.reduce((acc, { quantity }) => acc + quantity, 0)
    },
    roundedProductsQuantity (): string {
      return `${this.productsQuantity > 99 ? '99+' : this.productsQuantity}`
    },
    promotions ({ cart }): LineItems {
      return cart?.lineItems.filter(item => item.type === 'promotion' || item.type === 'bpgiftcard') || []
    },
    isEmpty (): boolean {
      return this.products.length === 0
    },
    hasPromotions (): boolean {
      return this.promotions.length !== 0
    },
    totalPrice ({ cart }): number {
      return cart?.price.totalPrice as number
    },
    currentShipping ({ cart }): Delivery {
      return cart?.deliveries[0] as Delivery
    },
    currentShippingMethod (): ShippingMethod {
      return this.currentShipping?.shippingMethod
    },
    currentShippingCountryId (): Delivery['location']['country']['id'] {
      return this.currentShipping?.location.country.id
    },
    currentShippingMethodId (): string {
      return this.currentShippingMethod?.id
    },
    addresses (): Array<Address> {
      const { data: user } = useAuth()
      return user.value?.addresses || []
    },
    defaultShippingAddressId (): Address['id'] | undefined {
      const { data: user } = useAuth()
      return user.value?.defaultShippingAddressId
    },
    lockerAddressId (): InpostAddress['id'] | undefined {
      const { data: user } = useAuth()
      return user.value?.customFields?.default_inpost_parcel_point_address_id
    },
    lockerAddress (): InpostAddress | null {
      return this.addresses.find(address => (
        address.id === this.lockerAddressId &&
        address.customFields?.bp_furgonetka_customer_address_inpost_parcel_point_address_flag === true &&
        typeof address.customFields.bp_furgonetka_customer_address_inpost_parcel_point_point_identifier === 'string' &&
        address.customFields.bp_furgonetka_customer_address_inpost_parcel_point_point_identifier !== ''
      )) as InpostAddress || null
    },
    currentPaymentMethodId ({ cart }): string {
      return cart?.transactions[0].paymentMethodId as string
    }
  }
})
